import { BoxGeometry, Group, Mesh } from 'three';
import type { HouseConfig, SlabHoleConfig } from '@/model/schema';
import type { MaterialKit } from '../materials';

/**
 * Lot base plate. Centred at origin, with the house body stacked on top.
 *
 * Holes
 * -----
 * When a basement is present the lot ground above the basement well
 * needs a hole so the user can actually see down into the basement
 * when peeling the L0 slab. Without this the lot base plate is opaque
 * and the user reports "the basement isn't there — is there a second
 * base plate hiding it?". (There was.)
 *
 * Hole splitting uses the same 4-strip pattern as the story floor.
 */
export function buildBasePlate(cfg: HouseConfig, kit: MaterialKit): Group {
  const group = new Group();
  group.name = 'BasePlate';

  const w = cfg.lot.width;
  const d = cfg.lot.depth;
  const t = cfg.lot.baseThickness;
  const y = -t / 2;

  // Holes are stored in lot-LL coords (origin at lot's lower-left corner).
  const holes: SlabHoleConfig[] = [];
  if (cfg.basement) {
    holes.push({
      x: cfg.basement.stair.x + cfg.house.originX + w / 2,
      z: cfg.basement.stair.z + cfg.house.originZ + d / 2,
      width: cfg.basement.stair.width,
      depth: cfg.basement.stair.depth,
    });
  }

  if (holes.length === 0) {
    addStrip(group, kit, w, d, w, d, 0, 0, y, t, 'BasePlate_full');
    return group;
  }

  for (let i = 0; i < holes.length; i++) {
    const h = holes[i]!;
    const hxLo = clamp(h.x, 0, w);
    const hxHi = clamp(h.x + h.width, 0, w);
    const hzLo = clamp(h.z, 0, d);
    const hzHi = clamp(h.z + h.depth, 0, d);

    if (hzLo > 0.01) {
      addStrip(group, kit, w, d, w, hzLo, 0, 0, y, t, `BasePlate_${i}_front`);
    }
    if (d - hzHi > 0.01) {
      addStrip(group, kit, w, d, w, d - hzHi, 0, hzHi, y, t, `BasePlate_${i}_back`);
    }
    if (hxLo > 0.01 && hzHi - hzLo > 0.01) {
      addStrip(group, kit, w, d, hxLo, hzHi - hzLo, 0, hzLo, y, t, `BasePlate_${i}_left`);
    }
    if (w - hxHi > 0.01 && hzHi - hzLo > 0.01) {
      addStrip(group, kit, w, d, w - hxHi, hzHi - hzLo, hxHi, hzLo, y, t, `BasePlate_${i}_right`);
    }
  }

  return group;
}

/** Add a base-plate strip in lot-local (origin-centered) coords. The strip
 *  is sized `sx` × `sz`, anchored at lot-LL `(xLo, zLo)`, and the lot's
 *  full size is passed as `(lotW, lotD)` to convert to centered coords. */
function addStrip(
  parent: Group,
  kit: MaterialKit,
  lotW: number,
  lotD: number,
  sx: number,
  sz: number,
  xLo: number,
  zLo: number,
  cy: number,
  thickness: number,
  name: string,
): void {
  if (sx <= 0 || sz <= 0) return;
  const mesh = new Mesh(new BoxGeometry(sx, thickness, sz), kit.outdoorFloor);
  mesh.name = name;
  mesh.receiveShadow = true;
  mesh.castShadow = false;
  mesh.position.set(xLo + sx / 2 - lotW / 2, cy, zLo + sz / 2 - lotD / 2);
  parent.add(mesh);
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}
